import { Component } from "react"
import PropTypes from "prop-types"
import { get, first, isEmpty } from "lodash"
import { CONTENT_SET, SEGMENT } from "helpers/shapes"
import { getBackwardsCompatibleSelectedType } from "helpers/comparison"
import ComparisonSettings from "modules/comparison-settings"
import CompareTopicSummary from "modules/compare-topic-summary"
import ComparePerformance from "modules/compare-performance"
import CompareRisk from "modules/compare-risk"
import CompareExposure from "modules/compare-exposure"
import CompareComposition from "modules/compare-composition"
import SmartTradePanel from "modules/smart-trade-panel"
import AddOptions from "./components/add-options"
import SaveExperiment from "modules/save-experiment"
import HedgeOptions from "./components/hedge-options"
import CompareSegments from "modules/compare-segments-root"
import CompareFactorSensitivity from "modules/compare-factor-sensitivity"
import { isMetricFactorSensitivity, getFactorSensitivityTypeFromMetric } from "helpers/factorSensitivity"
import { DOWNLOAD_FILENAME } from "helpers/shapes/compare-screen"
import "./style.scss"

function getHedgeDialogTitle(quickHedge) {
  if (quickHedge) {
    return "Add Index, ETF, or Single Security Hedge"
  }
  return "Set hedge options"
}

export default class ResearchTopic extends Component {
  static propTypes = {
    account: PropTypes.string.isRequired,
    modelId: PropTypes.string.isRequired,
    comparisonItems: PropTypes.array.isRequired,
    earliestDate: PropTypes.string.isRequired,
    latestDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    period: PropTypes.string.isRequired,
    setPeriod: PropTypes.func.isRequired,
    setSelectedComparisonType: PropTypes.func.isRequired,
    selectedComparisonType: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    selectedDate: PropTypes.string.isRequired,
    resetSelectedDate: PropTypes.func.isRequired,
    openDialog: PropTypes.func.isRequired,
    searchItemsActions: PropTypes.object.isRequired,
    trackExperimentTypeSelection: PropTypes.func.isRequired,
    trackAddExperiment: PropTypes.func.isRequired,
    currentTopicId: PropTypes.string.isRequired,
    nonOverlappingItems: PropTypes.array.isRequired,
    removeNonOverlappingItems: PropTypes.func.isRequired,
    setSelectedDate: PropTypes.func.isRequired,
    saveMetricToStore: PropTypes.func.isRequired,
    saveCompositionGroupToStore: PropTypes.func.isRequired,
    savedCompositionGroup: PropTypes.string.isRequired,
    saveTopicSummaryGroupToStore: PropTypes.func.isRequired,
    savedTopicSummaryGroup: PropTypes.string.isRequired,
    savedMetrics: PropTypes.object.isRequired,
    trackPanelTrigger: PropTypes.func.isRequired,
    comparisonTypes: PropTypes.array.isRequired,
    setTopicSummaryDate: PropTypes.func.isRequired,
    topicSummaryDate: PropTypes.string,
    baseUrl: PropTypes.string.isRequired,
    contentSets: PropTypes.arrayOf(CONTENT_SET).isRequired,
    summaryPanelOpen: PropTypes.bool.isRequired,
    toggleSummaryPanel: PropTypes.func.isRequired,
    toggleRenameExperiment: PropTypes.func.isRequired,
    selectedExperiment: PropTypes.object,
    selectedSegment: SEGMENT,
    experimentToUpdate: PropTypes.object,
    instrumentDescriptors: PropTypes.object,
    instrument: PropTypes.object,
    toggleHedgeItem: PropTypes.func,
    hedgeItem: PropTypes.object,
    currentHedges: PropTypes.array,
    addHedge: PropTypes.func,
    editHedge: PropTypes.func,
    removeHedge: PropTypes.func,
    addQuickHedge: PropTypes.func,
    closeDialog: PropTypes.func,
    showNotification: PropTypes.func,
    modelName: PropTypes.string.isRequired,
    setComparisonItems: PropTypes.func.isRequired,
    instrumentPortfolio: PropTypes.object,
    instrumentPortfolioPositions: PropTypes.object,
    fileName: DOWNLOAD_FILENAME.isRequired,
  }

  componentDidMount() {
    const { nonOverlappingItems, removeNonOverlappingItems, comparisonItems, showNotification, modelName } = this.props
    if (nonOverlappingItems.length > 0) {
      removeNonOverlappingItems(nonOverlappingItems)
    }

    if (get(comparisonItems, "0.isUnmodeled")) {
      showNotification("error", null, `Reference instrument is not available for ${modelName}, please change the model to properly view this research topic. `)
    }
  }

  componentDidUpdate(prevProps) {
    const { nonOverlappingItems, removeNonOverlappingItems, toggleRenameExperiment, experimentToUpdate, toggleHedgeItem, hedgeItem } = this.props

    if (toggleRenameExperiment && experimentToUpdate && prevProps.experimentToUpdate !== experimentToUpdate) {
      this.renameExperiment(experimentToUpdate)
    }

    if (toggleHedgeItem && hedgeItem && prevProps.hedgeItem !== hedgeItem) {
      this.setHedgeItem(hedgeItem)
    }

    if (nonOverlappingItems.length > 0) {
      removeNonOverlappingItems(nonOverlappingItems)
    }
  }

  renameExperiment = (experiment) => {
    const { openDialog } = this.props
    openDialog({
      title: `Update Experiment : ${experiment.name} `,
      content: <SaveExperiment isEditMode experiment={experiment} submitLabel="Update Experiment" />,
    })
  }

  toggleSaveExperiment = () => {
    const { openDialog, currentTopicId } = this.props
    openDialog({
      title: "Create Experiment",
      content: <SaveExperiment experiment={{ type: "PORTFOLIO" }} researchTopicId={currentTopicId} submitLabel="Upload Experiment" />,
    })
  }

  setHedgeItem = (item, quickHedge = false) => {
    const { openDialog, account, modelId, currentTopicId, currentHedges, addHedge, editHedge, removeHedge, addQuickHedge, closeDialog } = this.props
    openDialog({
      title: getHedgeDialogTitle(quickHedge),
      content: <HedgeOptions
        account={account}
        researchTopicId={currentTopicId}
        modelId={modelId}
        currentHedges={currentHedges}
        comparisonItem={item}
        addHedge={addHedge}
        editHedge={editHedge}
        removeHedge={removeHedge}
        addQuickHedge={addQuickHedge}
        quickHedge={quickHedge}
        closeDialog={closeDialog}
      />,
    })
  }

  openCreateInstrumentHedge = () => {
    const { comparisonItems } = this.props

    this.setHedgeItem(first(comparisonItems), true)
  }

  render() {
    const {
      account,
      modelId,
      comparisonItems,
      earliestDate,
      latestDate,
      endDate,
      startDate,
      period,
      setPeriod,
      selectedDate,
      resetSelectedDate,
      setSelectedDate,
      openDialog,
      searchItemsActions,
      trackExperimentTypeSelection,
      trackAddExperiment,
      currentTopicId,
      saveMetricToStore,
      saveCompositionGroupToStore,
      savedMetrics,
      trackPanelTrigger,
      comparisonTypes,
      selectedComparisonType,
      setSelectedComparisonType,
      savedCompositionGroup,
      setTopicSummaryDate,
      topicSummaryDate,
      savedTopicSummaryGroup,
      saveTopicSummaryGroupToStore,
      baseUrl,
      contentSets,
      summaryPanelOpen,
      toggleSummaryPanel,
      selectedExperiment,
      selectedSegment,
      instrumentDescriptors,
      setComparisonItems,
      instrumentPortfolio,
      instrumentPortfolioPositions,
      fileName,
    } = this.props

    const addOptions = <AddOptions
      openDialog={openDialog}
      account={account}
      modelId={modelId}
      researchTopicId={currentTopicId}
      searchItemsActions={searchItemsActions}
      disableSearchResult={(item) => item.type !== "experiment"}
      trackExperimentTypeSelection={trackExperimentTypeSelection}
      trackAddExperiment={trackAddExperiment}
      toggleSaveExperiment={this.toggleSaveExperiment}
      openCreateInstrumentHedge={this.openCreateInstrumentHedge}
      isInstrumentPortfolio={!isEmpty(instrumentPortfolio)}
    />

    const { id: selectedId, type } = getBackwardsCompatibleSelectedType(selectedComparisonType, "summary")
    const isContentSet = type === "contentset"
    const saveExposureMetric = isContentSet ? selectedId : "exposure"

    return <div className="modules_research-topic">
      <CompareSegments
        account={account}
        modelId={modelId}
        comparisonItems={comparisonItems}
        endDate={endDate}
        startDate={startDate}
        setParentComparisonItems={(items) => setComparisonItems(items)}
      >
        <ComparisonSettings
          account={account}
          modelId={modelId}
          selectedComparisonType={selectedComparisonType}
          comparisonItems={comparisonItems}
          addOptions={addOptions}
          selectedDate={selectedId === "summary" ? topicSummaryDate : selectedDate}
          earliestDate={earliestDate}
          latestDate={latestDate}
          endDate={endDate}
          startDate={startDate}
          period={period}
          setPeriod={setPeriod}
          resetDate={resetSelectedDate}
          comparisonTypes={comparisonTypes}
          setSelectedDate={setTopicSummaryDate}
          setSelectedComparisonType={setSelectedComparisonType}
          singleDateSelector={selectedId === "summary"}
          contentSets={contentSets}
          baseUrl={baseUrl}
        />
      </CompareSegments>
      {selectedId === "summary" && <CompareTopicSummary
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        selectedDate={topicSummaryDate}
        savedGroup={savedTopicSummaryGroup}
        saveGroupToStore={saveTopicSummaryGroupToStore}
        instrumentDescriptors={instrumentDescriptors}
        instrumentPortfolio={instrumentPortfolio}
        fileName={fileName}
      />}
      {selectedId === "performance" && <ComparePerformance
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        startDate={startDate}
        endDate={endDate}
        selectedDate={selectedDate}
        setDate={setSelectedDate}
        saveMetricToStore={saveMetricToStore}
        savedMetric={savedMetrics ? savedMetrics.performance : null}
        trackPanelTrigger={trackPanelTrigger}
      />}
      {selectedId === "risk" && <CompareRisk
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        startDate={startDate}
        endDate={endDate}
        selectedDate={selectedDate}
        setDate={setSelectedDate}
        saveMetricToStore={saveMetricToStore}
        savedMetric={savedMetrics ? savedMetrics.risk : null}
        trackPanelTrigger={trackPanelTrigger}
      />}
      {(selectedId === "exposure" || isContentSet) && <CompareExposure
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        startDate={startDate}
        endDate={endDate}
        selectedDate={selectedDate}
        setDate={setSelectedDate}
        saveMetricToStore={saveMetricToStore}
        savedMetric={savedMetrics ? savedMetrics[saveExposureMetric] : null}
        trackPanelTrigger={trackPanelTrigger}
        contentSetId={isContentSet ? selectedId : null}
      />}
      {selectedId === "composition" && <CompareComposition
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        startDate={startDate}
        endDate={endDate}
        selectedDate={selectedDate}
        setDate={setSelectedDate}
        saveMetricToStore={saveMetricToStore}
        saveGroupToStore={saveCompositionGroupToStore}
        savedMetric={savedMetrics ? savedMetrics.composition : null}
        savedGroup={savedCompositionGroup}
      />}
      {isMetricFactorSensitivity(selectedId) && <CompareFactorSensitivity
        factorSensitivityType={getFactorSensitivityTypeFromMetric(selectedId)}
        account={account}
        modelId={modelId}
        positionSets={comparisonItems}
        startDate={startDate}
        endDate={endDate}
        selectedDate={selectedDate}
        setDate={setSelectedDate}
        saveMetricToStore={saveMetricToStore}
        savedMetric={savedMetrics ? get(savedMetrics, selectedId) : null}
        trackPanelTrigger={trackPanelTrigger}
      />}
      {selectedExperiment && <SmartTradePanel
        instrumentDescriptors={instrumentDescriptors}
        instrumentPortfolio={instrumentPortfolio}
        instrumentPortfolioPositions={instrumentPortfolioPositions}
        selectedDate={selectedId === "summary" ? topicSummaryDate : selectedDate}
        experiment={selectedExperiment}
        segment={selectedSegment}
        readOnly
        panelOpen={summaryPanelOpen}
        toggleOpenPanel={toggleSummaryPanel}
      />}
    </div>
  }
}
